import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useMappedFields } from "../../hooks/useMappedFields";
import { MappedField } from "../../interfaces";
import { AppContext } from "../../context/index";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { Controller, useForm } from "react-hook-form";
import { Button, InputText } from "iuvity-ui-components";
import { Column } from 'primereact/column';
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from 'primereact/multiselect';
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const formBlankValues = {
  name: "",
  description: "",
  labelkey: "",
  descriptionlabelkey: "",
  serviceapi: "",
  datatype: "",
  dashboardoption: "",
  releaseversion: "",
};

export const MappedFieldPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, user, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          name: pageFilter?.name || "",
          description: pageFilter?.description || "",
          labelkey: pageFilter?.labelkey || "",
          descriptionlabelkey: pageFilter?.descriptionlabelkey || "",
          serviceapi: pageFilter?.serviceapi || "",
          datatype: pageFilter?.datatype || "",
          dashboardoption: pageFilter?.dashboardoption || "",
          releaseversion: pageFilter?.releaseversion || "",
        }
      : formBlankValues,
  });

  const { control, setValue } = form;

  const { data, mappedFieldAction, reloadFn } = useMappedFields("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("MappedFields.nameLabel"),
        },
        {
          field: "description",
          header: t("MappedFields.descriptionLabel"),
        },
        {
          field: "labelkey",
          header: t("MappedFields.labelkeyLabel"),
        },
        {
          field: "descriptionlabelkey",
          header: t("MappedFields.descriptionlabelkeyLabel"),
        },
        {
          field: "serviceapi",
          header: t("MappedFields.serviceapiLabel"),
        },
        {
          field: "datatype",
          header: t("MappedFields.datatypeLabel"),
        },
        {
          field: "dashboardoption",
          header: t("MappedFields.dashboardoptionLabel"),
        },
        {
          field: "releaseversion",
          header: t("MappedFields.releaseversionLabel"),
        },
        {
          field: "actions",
          header: t("MappedFields.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);
  
  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: pageFilter?.name, matchMode: FilterMatchMode.IN },
    description: { value: pageFilter?.description, matchMode: FilterMatchMode.CONTAINS },
    labelkey: { value: pageFilter?.labelkey, matchMode: FilterMatchMode.IN },
    descriptionlabelkey: { value: pageFilter?.descriptionlabelkey, matchMode: FilterMatchMode.CONTAINS },
    serviceapi: { value: pageFilter?.serviceapi, matchMode: FilterMatchMode.IN },
    datatype: { value: pageFilter?.datatype, matchMode: FilterMatchMode.IN },
    dashboardoption: { value: pageFilter?.dashboardoption, matchMode: FilterMatchMode.IN },
    releaseversion: { value: pageFilter?.releaseversion, matchMode: FilterMatchMode.IN },
  };

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const actionDetailFn = (mappedField: MappedField) => {
    setAppState((prev) => ({
      ...prev,
      selectedMappedField: mappedField,
    }));
    navigate(`${location.pathname}/detail`);
    window.scrollTo(0, 0);
  };

  const actionEditFn = (mappedField: MappedField) => {
    setAppState((prev) => ({
      ...prev,
      selectedMappedField: mappedField,
    }));
    navigate(`${location.pathname}/edit`);
    window.scrollTo(0, 0);
  };

  const actionDeleteFn = async (mappedField: MappedField) => {
    await mappedFieldAction("DELETE", mappedField.id);
    reloadFn();
  };

  const handleModalProps = async (mappedField: MappedField) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("MappedFields.deleteModalTitle"),
      content: `${t("MappedFields.deleteModalContent1")} "${
        mappedField?.name
      }" ${t("MappedFields.deleteModalContent2")}`,
      onConfirm: () => actionDeleteFn(mappedField),
    }));
  };

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`MappedFields.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`MappedFields.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`MappedFields.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.map((item: any) => (
        {
          label: item[`${options.field}`], 
          value: item[`${options.field}`]
        }))
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`MappedFields.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`MappedFields.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("MappedFields.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.applyFilterLabel")}
        className="p-button"
        onClick={()=>{
          options.filterApplyCallback();
          setAppState((prev: any)=>({
            ...prev,
            // pageFilter: getValues(),
          }));
        }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFiltersColumn}
      />
    )
  };

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("MappedFields.mappedFieldsTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("MappedFields.mappedFieldsTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("MappedFields.mappedFieldsDescription")}</span>
          <span>{t("MappedFields.mappedFieldsDescription2")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("MappedFields.newMappedField"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t("MappedFields.mappedFieldsTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            filters={filters}
            value={data.map((mappedField) => ({
              ...mappedField,
              actions: (
                <Actions
                  enableDelete={!!mappedField?.createdby}
                  onDetail={() => actionDetailFn(mappedField)}
                  onEdit={() => actionEditFn(mappedField)}
                  onDelete={() => handleModalProps(mappedField)}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['name', 'description', 'labelkey', 'descriptionlabelkey', 'serviceapi', 'datatype', 'dashboardoption', 'releaseversion']}
            filterDisplay="menu"
            stateStorage="session"
            stateKey="filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? { justifyContent: "center" }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8.3rem" : "14rem",
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={
                  ((col.field === "description" || col.field === "descriptionlabelkey") && filterTemplate) ||
                  ((col.field === "name" || col.field == "labelkey" || col.field == "serviceapi" || col.field == "datatype" || col.field == "dashboardoption" || col.field == "releaseversion") && multiselectFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
